import * as React from 'react'
import {Auth} from "aws-amplify";
import {useFormik} from 'formik';
import * as yup from 'yup';
import {TextField} from '../../common';
import {Button} from '../../common/Button';
import {Container, Grid} from "@mui/material";
import {PhoneNumberInput} from "../../common/PhoneNumberInput";
import DateInput from "../../common/DateInput";
import {phoneRegExp} from "../../helpers/validation.helpers";
import Error from "../../common/Error";
import {formatBirthdateForApi} from "../../helpers/display.helpers";
import {getCurrentStoreId} from "../../helpers/init.helpers";

export interface SignUpRequest {
  given_name: string
  family_name: string
  birthdate: string
  zip_code: string
  phone_number: string
  email: string
  password: string
}

const validationSchema = yup.object({
  given_name: yup
    .string()
    .required('First name is required'),
  family_name: yup
    .string()
    .required('Last name is required'),
  birthdate: yup
    .string()
    .min(10, 'Birthdate is not valid')
    .required('Birthdate is required'),
  phone_number: yup
    .string()
    .matches(phoneRegExp, 'Phone number is not valid')
    .required('Phone number is required'),
  zip_code: yup
    .string()
    .min(5, 'Zip code is not valid')
    .required('Zip code is required'),
  email: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password is required'),
});

const SignUpForm = (props: { onSuccess?: (username:string) => void }) => {
  const [error, setError] = React.useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      given_name: '',
      family_name: '',
      birthdate: '',
      zip_code: '',
      email: '',
      phone_number: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values: SignUpRequest) => {
      try {
        const data = {
          username: values.email,
          password: values.password,
          attributes: {
            given_name: values.given_name,
            family_name: values.family_name,
            phone_number: `+1${values.phone_number}`,
            birthdate: formatBirthdateForApi(values.birthdate),
            "custom:zipcode": values.zip_code,
            "custom:dispensary": getCurrentStoreId(),
          },
        };

        await Auth.signUp(data);

        if (props.onSuccess) {
          props.onSuccess(data.username);
        }

      } catch (error) {
        // @ts-ignore
        setError(error.message);
        console.log(error);
      }
    },
  });

  return <>
    <Container maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            id="given_name"
            name="given_name"
            label="First Name"
            value={formik.values.given_name}
            onChange={formik.handleChange}
            error={formik.touched.given_name && Boolean(formik.errors.given_name)}
            helperText={formik.touched.given_name && formik.errors.given_name}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="family_name"
            name="family_name"
            label="Last Name"
            value={formik.values.family_name}
            onChange={formik.handleChange}
            error={formik.touched.family_name && Boolean(formik.errors.family_name)}
            helperText={formik.touched.family_name && formik.errors.family_name}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="email"
            name="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="phone_number"
            name="phone_number"
            label="Phone number"
            InputProps={{inputComponent: PhoneNumberInput as any}}
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
            helperText={formik.touched.phone_number && formik.errors.phone_number}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="birthdate"
            name="birthdate"
            label="Birth date"
            placeholder="mm/dd/yyyy"
            InputProps={{inputComponent: DateInput as any}}
            value={formik.values.birthdate}
            onChange={formik.handleChange}
            error={formik.touched.birthdate && Boolean(formik.errors.birthdate)}
            helperText={formik.touched.birthdate && formik.errors.birthdate}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="zip_code"
            name="zip_code"
            label="Zip Code"
            value={formik.values.zip_code}
            onChange={formik.handleChange}
            error={formik.touched.zip_code && Boolean(formik.errors.zip_code)}
            helperText={formik.touched.zip_code && formik.errors.zip_code}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="password"
            name="password"
            label="Password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>
        <Grid item xs={12}>
          <Button color="primary" variant="contained" fullWidth type="submit" disabled={formik.isSubmitting}>
            Create account
          </Button>
        </Grid>
      </Grid>

      {error &&
      <Error>{error}</Error>
      }
      </form>
    </Container>
  </>

}

export default SignUpForm;